import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import RightArrowIcon from "@tsesco/Icons/RightArrowIcon";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isQtyAdjusted: boolean;
    multipleProducts: boolean;
    cartLines: CartLineModel[] | null;
}

export interface ProductAddedToCartMessageStyles {
    productNum?: TypographyPresentationProps;
    productPrice?: TypographyPresentationProps;
    messageWrapper?: InjectableCss;
    productPriceWrapper?: InjectableCss;
    productListWrapper?: InjectableCss;
    productWrapper?: InjectableCss;
    productImageDetailsWrapper?: InjectableCss;
    productImageWrap?: InjectableCss;
    defaultText?: TypographyPresentationProps;
    quantityAdjustedText?: TypographyPresentationProps;
    headerText?: TypographyPresentationProps;
    imgStyle?: LazyImageProps;
    iconTextWrapper?: InjectableCss;
    iconStyle?: InjectableCss;
    buttonWrapper?: InjectableCss;
    saveAndContinueButton?: ButtonPresentationProps;
    multipleSaleQtyText?: TypographyPresentationProps;
}

export const productAddedToCartMessageStyles: ProductAddedToCartMessageStyles = {
    iconTextWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-top: 0px;
        `,
    },
    iconStyle: {
        css: css`
            height: 22.5px;
            width: 31.5px;
            svg {
                height: 18.5px;
                width: 18.5px;
            }
        `,
    },
    headerText: {
        variant: "h2",
        css: css`
            font-size: 1.125rem;
            margin-bottom: 0px;
            color: ${getColor("text.accent")};
        `,
    },
    productNum: {
        variant: "p",
        css: css`
            /* margin-bottom: 8px; */
            font-weight: 600;
            font-size: 14px;
            line-height: 16px;
            color: ${getColor("secondary")};
            display: flex;
            align-items: center;
            margin-left: 20px;
            span[class^="TypographyStyle-sc"] {
                color: ${getColor("secondary")};
            }
        `,
    },
    productPrice: {
        variant: "p",
        css: css`
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
            color: ${getColor("text.accent")};
        `,
    },
    productWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    productImageWrap: {
        css: css`
            flex-basis: 30%;
            div {
                width: 97.5px;
                height: 97.5px;
                background: #fff;
                /* box-shadow: 0px 0px 1px rgba(38, 37, 37, 0.56), 0px 2px 8px rgba(38, 37, 37, 0.16); */
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 40%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    productPriceWrapper: {
        css: css`
            display: flex;
            align-items: center;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 60%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    productListWrapper: {
        css: css`
            max-height: 200px;
            padding: 25px 10px;
            /* border-top: 2px solid gray; */
            border-bottom: 0.5px solid #6c6e71;
            > .productWrapper:not(:first-child) {
                margin-top: 15px;
            }
        `,
    },
    productImageDetailsWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },
    defaultText: {
        variant: "p",
    },
    quantityAdjustedText: {
        variant: "p",
    },
    imgStyle: {
        css: css`
            padding: 0px;
        `,
    },
    saveAndContinueButton: {
        variant: "primary",
        css: css`
            margin-top: 20px;
            width: 100%;
            font-weight: 500;
            font-family: "Roboto-Medium";
            ${TypographyStyle} {
                font-size: 14px;
                font-weight: normal;
                font-family: "Roboto-Medium";
            }
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    multipleSaleQtyText: {
        fontFamily: "Roboto-Bold",
        css: css`
            text-align: center;
            display: flex;
            justify-content: center;
            margin-top: 20px;
            font-size: 14px;
        `,
    },
};

const mapStateToProps = (state: ApplicationState) => ({
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url || "",
    orderGrandTotalDisplay: getCurrentCartState(state).value?.orderGrandTotalDisplay,
});

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps>;
const styles = productAddedToCartMessageStyles;

const ProductAddedToCartMessage = ({
    isQtyAdjusted,
    multipleProducts,
    cartLines,
    cartUrl,
    orderGrandTotalDisplay,
    history,
}: Props) => {
    const defaultMessage = multipleProducts
        ? siteMessage("Cart_AllProductsAddedToCart")
        : siteMessage("Cart_ProductAddedToCart");

    const goToCartPage = () => {
        // history.push("/Cart");
        window.location.href = "/Cart";
    };
    if (!cartLines) {
        return <Typography {...styles.defaultText}>{defaultMessage}</Typography>;
    }
    // const multipleSaleQty = cartLines[0].qtyPerBaseUnitOfMeasure;

    return (
        <StyledWrapper {...styles.messageWrapper}>
            <StyledWrapper {...styles.iconTextWrapper}>
                <Icon src={RightArrowIcon} {...styles.iconStyle} />
                <Typography {...styles.headerText}>{translate("Added To Cart!")}</Typography>
            </StyledWrapper>
            {isQtyAdjusted && (
                <Typography {...styles.multipleSaleQtyText}>
                    {translate("The quantity added to the cart was changed due to rounding or minimum quantity rules.")}
                </Typography>
            )}

            <StyledWrapper {...styles.productListWrapper}>
                {cartLines &&
                    cartLines.map(
                        cartline =>
                            cartline && (
                                <StyledWrapper
                                    key={cartline.erpNumber}
                                    {...styles.productWrapper}
                                    className="productWrapper"
                                >
                                    <StyledWrapper {...styles.productImageDetailsWrapper}>
                                        <StyledWrapper {...styles.productImageWrap}>
                                            <LazyImage
                                                {...styles.imgStyle}
                                                src={cartline.smallImagePath}
                                                altText={cartline.altText}
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.productPriceWrapper}>
                                            <Link onClick={goToCartPage} {...styles.productNum}>
                                                {cartline.shortDescription}
                                            </Link>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            ),
                    )}
            </StyledWrapper>
            <StyledWrapper {...styles.buttonWrapper}>
                <Button {...styles.saveAndContinueButton} onClick={goToCartPage}>
                    {translate("View Cart & Checkout")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withHistory(ProductAddedToCartMessage));
